import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, CalendarDays, Clock, ChevronRight, ExternalLink, ArrowRight } from "lucide-react";
import { fetchEvent } from "@/lib/api";
import { Reveal } from "@/lib/motion";

export default function EventoDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchEvent(slug).then((e) => { setEv(e); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-40 text-center text-[#B8B8B8]">Carregando…</div>;
  if (!ev) return <div className="pt-40 text-center text-[#B8B8B8]">Evento não encontrado.</div>;

  const d = new Date(ev.date + "T12:00:00");
  const dateStr = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid={`event-detail-${ev.slug}`}>
      <section className="relative h-[68vh] min-h-[500px] w-full overflow-hidden flex items-end">
        <img src={ev.cover_image} alt={ev.name} className="absolute inset-0 w-full h-full object-cover slow-zoom" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/30 via-[#0B0B0B]/40 to-[#0B0B0B]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/85 via-transparent to-transparent" />
        <div className="container-rotta relative z-10 pb-16 md:pb-20">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-4 flex items-center gap-2">
            <Link to="/eventos" className="hover:text-[#C6A762]">Eventos</Link>
            <ChevronRight size={12} /><span className="text-[#C6A762]">{ev.state}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white leading-[0.95]">{ev.name}</h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-white/85 mt-6">
            <span className="flex items-center gap-2"><CalendarDays size={14} className="text-[#C6A762]" /> {dateStr}</span>
            {ev.time && <span className="flex items-center gap-2"><Clock size={14} className="text-[#C6A762]" /> {ev.time}</span>}
            <span className="flex items-center gap-2"><MapPin size={14} className="text-[#C6A762]" /> {ev.city}, {ev.state}</span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rotta grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="flex items-center gap-3 text-[#C6A762] mb-5"><span className="gold-divider" /><span className="text-[11px] tracking-[0.35em] uppercase">Sobre o evento</span></div>
              <p className="text-[#D6D6D6] leading-relaxed text-lg whitespace-pre-line">
                {ev.description || ev.short_description}
              </p>
            </Reveal>
          </div>
          <aside className="lg:col-span-4">
            <div className="card-rotta p-7 bg-[#141414] sticky top-24">
              <div className="font-display tracking-wider uppercase text-sm text-white mb-5">Informações</div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-3 text-[#B8B8B8]"><CalendarDays size={15} className="text-[#C6A762]" /> {dateStr}</li>
                {ev.time && <li className="flex items-center gap-3 text-[#B8B8B8]"><Clock size={15} className="text-[#C6A762]" /> {ev.time}</li>}
                <li className="flex items-center gap-3 text-[#B8B8B8]"><MapPin size={15} className="text-[#C6A762]" /> {ev.city}, {ev.state}</li>
              </ul>
              {ev.registration_url && (
                <a href={ev.registration_url} target="_blank" rel="noreferrer" data-testid="event-register-link" className="btn-gold w-full mt-6 py-3.5 rounded-full text-xs inline-flex items-center justify-center gap-2">
                  Inscrição oficial <ExternalLink size={13} />
                </a>
              )}
            </div>
          </aside>
        </div>
      </section>

      <section className="section pt-0">
        <div className="container-rotta">
          <Link to="/eventos" className="text-[#C6A762] text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 hover:translate-x-1 transition-transform">
            <ArrowRight size={14} className="rotate-180" /> Ver todos os eventos
          </Link>
        </div>
      </section>
    </div>
  );
}
